import React, { useState } from 'react';
import {
  Box, Typography, Paper, Stack, Table, TableHead, TableRow, TableCell, TextField, Button, Snackbar, Alert,
  Divider, IconButton, Tooltip,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import DemoDataSettings from '../components/DemoDataSettings';
import MarketingSendingCard from '../components/MarketingSendingCard';
import {
  useCompanyDetails, useSaveCompanyDetails, getLegacyBrowserDetails, clearLegacyBrowserDetails,
} from '../utils/companyDetails';

// The original sample details, restored by "Reset to factory defaults"
const FACTORY_DEFAULTS = {
  name: 'Matrimony Services Pvt. Ltd.', brandName: 'Matrimony', address: '123, Anna Salai, Chennai – 600 002',
  phone: '+91 44 1234 5678', email: 'contracts@matrimony.app', website: 'www.matrimony.app',
  gstin: '33AABCM1234A1Z5', cin: 'U74900TN2000PTC123456',
};

const FIELDS = [
  { key: 'name',    label: 'Company Name',    hint: 'Legal registered name — shown on contracts, print views and the home page' },
  { key: 'brandName', label: 'Display Name',  hint: 'Short name shown in the app header, browser tab, sign-up page and emails (e.g. "Matrimony"). Leave empty to use the company name.' },
  { key: 'address', label: 'Address',         hint: 'Registered office address' },
  { key: 'phone',   label: 'Phone',           hint: 'Primary contact number with ISD code' },
  { key: 'email',   label: 'Email',           hint: 'Official contact / contracts email' },
  { key: 'website', label: 'Website',         hint: 'Without https://' },
  { key: 'gstin',   label: 'GSTIN',           hint: '15-character GST Identification Number' },
  { key: 'cin',     label: 'CIN',             hint: 'Corporate Identification Number' },
  { key: 'appUrl',  label: 'App public address', hint: 'The address people open this app at, e.g. https://kalyanaservices.in. Every link in emails, WhatsApp, SMS and invitations (sign-in, vendor invitations, surveys, wedding invitations) uses it. Leave empty to use APP_URL from the server settings.' },
];

export default function AdminCompanyPage() {
  const company = useCompanyDetails();
  const [saveDetails, { loading: saving }] = useSaveCompanyDetails();
  const [legacy, setLegacy] = useState(getLegacyBrowserDetails);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const startEdit = () => {
    setForm({ ...company });
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm({});
    setEditing(false);
  };

  const persist = async (details, msg, severity = 'success') => {
    try {
      await saveDetails(details);
      setEditing(false);
      setForm({});
      setSnack({ open: true, msg, severity });
      return true;
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: 'error' });
      return false;
    }
  };

  const handleSave = () => {
    // Basic validation (the server checks the same)
    if (!form.name?.trim()) {
      setSnack({ open: true, msg: 'Company name is required.', severity: 'error' });
      return;
    }
    if (!form.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setSnack({ open: true, msg: 'Enter a valid email address.', severity: 'error' });
      return;
    }
    persist(form, 'Company details saved. They now show across the whole application for everyone.');
  };

  // The app's address is kept: resetting it would break every link sent from now on
  const handleReset = () => persist({ ...FACTORY_DEFAULTS, appUrl: company.appUrl || '' }, 'Company details reset to defaults.', 'info');

  // One-time move of details the old version saved only in this browser
  const importLegacy = async () => {
    if (await persist({ ...company, ...legacy }, 'Details from this browser are now saved for everyone.')) {
      clearLegacyBrowserDetails();
      setLegacy(null);
    }
  };
  const dismissLegacy = () => { clearLegacyBrowserDetails(); setLegacy(null); };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <BusinessIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Company Details</Typography>
            <Typography variant="body2" color="text.secondary">
              Company information shown across the whole application — header, home page, contracts, print views and emails.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {!editing && (
            <>
              <Button variant="contained" startIcon={<EditIcon />} onClick={startEdit}>
                Edit Details
              </Button>
              <Tooltip title="Reset to factory defaults">
                <IconButton onClick={handleReset} color="warning">
                  <RestoreIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {editing && (
            <>
              <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                Save
              </Button>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={cancelEdit}>
                Cancel
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {legacy && legacy.name && legacy.name !== company.name && (
        <Alert severity="warning" sx={{ mb: 3 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={importLegacy} disabled={saving}>Save for everyone</Button>
              <Button color="inherit" size="small" onClick={dismissLegacy}>Discard</Button>
            </Stack>
          }>
          This browser has company details saved by the previous version of this page (<strong>{legacy.name}</strong>),
          which only this browser could see. Save them for everyone, or discard them.
        </Alert>
      )}

      {company.appUrlIsLocal && (
        <Alert severity="warning" sx={{ mb: 3 }} action={!editing && <Button color="inherit" size="small" onClick={startEdit}>Set it</Button>}>
          Links the app sends (sign-in, invitations, surveys, WhatsApp buttons) point at <b>{company.effectiveAppUrl}</b>, which only works on this
          computer or network. Set the <b>App public address</b> to the address people use, e.g. your domain.
        </Alert>
      )}

      {/* View mode — table */}
      {!editing && (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: '#1a237e', px: 3, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700} color="white" letterSpacing={1}>
              {company.name}
            </Typography>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                {FIELDS.map(f => <TableCell key={f.key}>{f.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {FIELDS.map(f => (
                  <TableCell key={f.key}>
                    {f.key === 'appUrl' && !company.appUrl
                      ? <Typography variant="body2" color="text.secondary">{company.effectiveAppUrl} <i>(server setting)</i></Typography>
                      : company[f.key] || '—'}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
          </Box>
        </Paper>
      )}

      {/* Edit mode — form */}
      {editing && (
        <Paper elevation={2} sx={{ borderRadius: 2, p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>Edit Company Details</Typography>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={2.5}>
            {FIELDS.map(f => (
              <TextField
                key={f.key}
                label={f.label}
                helperText={f.hint}
                value={form[f.key] || ''}
                placeholder={f.key === 'appUrl' ? company.effectiveAppUrl : undefined}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                fullWidth
                size="small"
                required={f.key === 'name' || f.key === 'email'}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={2} mt={3} justifyContent="flex-end">
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={cancelEdit}>
              Cancel
            </Button>
            <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
              Save Changes
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Info note */}
      <Paper elevation={0} sx={{ mt: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7' }}>
        <Typography variant="body2" color="success.dark">
          <strong>Note:</strong> These details are saved for everyone and used across the whole application: the home page
          (header, company details and footer), the app header and browser tab, the sign-up page, contracts and their print
          view, and emails. Your screens update as soon as you save; other users' open pages update within 30 seconds or
          when they return to the tab.
        </Typography>
      </Paper>

      {/* Hide / show the seed (demo) accounts and their data */}
      <MarketingSendingCard />
      <DemoDataSettings />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
